// IMOTEpS — Centre de recherche / Chapitre 12
// Auto-split from chapters.js (Feb 2026 refactor)

export const chapter12 = {
  id: 12,
  slug: "hypersollicitation-jeune-sportif",
  color: "#C45C00",
  title: "Syndromes d'hypersollicitation chez le jeune sportif",
  subtitle: "Cartilage de croissance, charge d'entraînement, sommeil et nutrition",
  region: "Sport adolescent",
  readingMin: 25,
  summary:
    "30 à 50 % des blessures chez l'adolescent sont liées au surmenage. Cartilage de croissance (physis et apophyse), Little League Shoulder/Elbow, OCD du capitellum, biologie cellulaire, importance du sommeil profond N3, nutrition (protéines 1,2-2 g/kg/j, lait chocolaté), prévention.",
  audience: "Patients adolescents, parents et entraîneurs.",
  sections: [
    {
      heading: "1. Épidémiologie",
      list: [
        "30 à 50 % des blessures chez l'adolescent = surmenage",
        "30 % des blessures de surmenage touchent le membre supérieur",
        "Fractures de physis du membre supérieur : 2 fois plus que pour le membre inférieur",
        "Sommeil < 8 h : risque de blessure × 1,7 (Dwivedi et al.)",
      ],
    },
    {
      heading: "2. Le cartilage de croissance",
      body: "Physis (cartilage épiphysaire) : ossification endochondrale, zone la plus fragile de l'os en croissance. Apophyse (cartilage d'insertion tendineuse) : arrachement progressif par traction tendineuse répétée. Pic de vélocité staturale : 8 à 12 cm par an entre 10 et 14 ans — l'os grandit plus vite que les tendons et muscles, créant un déséquilibre transitoire. MYTHE : la musculation supervisée ne stoppe PAS la croissance (consensus AAP, ACSM).",
    },
    {
      heading: "3. Pathologies spécifiques",
      list: [
        "Little League Shoulder (11-16 ans) — physis proximale humérus — baseball, handball, volley",
        "Little League Elbow (9-14 ans) — apophyse épicondyle médial",
        "Gymnast's Wrist (10-16 ans) — physis distale du radius",
        "OCD du capitellum (11-15 ans) — cartilage articulaire — baseball, tennis",
        "Épicondylite précoce (14-18 ans)",
        "Tendinopathie de coiffe immature (13-18 ans, sports overhead)",
      ],
    },
    {
      heading: "4. Sommeil — la clé que personne n'enseigne",
      body: "Phase N3 (sommeil profond) : pic de sécrétion de l'hormone de croissance (GH), réparation musculaire, croissance osseuse. C'est ICI que le sportif progresse réellement. Phase REM : consolidation de la mémoire motrice. Effets d'une nuit < 8 h : GH↓, cortisol↑, glycogène non reconstitué, IGF-1↓, testostérone -24 %, cytokines pro-inflammatoires↑, blessure ×1,7. Recommandation : 8 à 10 heures par nuit pour les adolescents sportifs. Le chronotype adolescent est biologiquement décalé — ne pas forcer les entraînements matinaux.",
    },
    {
      heading: "5. Nutrition péri-effort",
      body: "Protéines : 1,2 à 2 g/kg/j répartis sur 3-4 repas. Glucides : 50 à 60 % des apports (glycogène = carburant). Lipides : 25 à 35 %, oméga-3 anti-inflammatoires. Calcium 1200-1500 mg/j, vitamine D, fer (filles + endurance), zinc, vitamine C (collagène). Timing : 2-3 h avant = glucides complexes, 30-60 min avant = banane/pain-confiture, pendant > 60-90 min = 30-60 g glucides/h + 150-250 ml d'eau/15 min, 0-30 min après = LAIT CHOCOLATÉ (ratio glucides/protéines optimal), soir = protéines lentes (caséine, fromage blanc).",
    },
    {
      heading: "6. Prévention — règle des 10 %",
      list: [
        "Ne jamais augmenter le volume d'entraînement de plus de 10 % par semaine",
        "Semaine allégée toutes les 3-4 semaines (deload week)",
        "Au moins 2 jours de repos par semaine",
        "Diversifier les sports jusqu'à 13-14 ans (recommandation AAP)",
        "Au moins 2 mois de repos complet par an",
        "ACWR (Gabbett Br J Sports Med 2016) : ratio charge aiguë/chronique ≤ 1,5",
      ],
    },
    {
      heading: "7. Signaux d'alarme",
      body: "🚨 Consulter immédiatement : douleur vive brutale en effort, craquement, gonflement rapide, douleur de physis, déformation, engourdissements. ⚠️ Consulter dans la semaine : douleur > 2 semaines malgré repos, baisse de performance inexpliquée, fatigue chronique, infections répétées, perte d'appétit + amaigrissement. Le syndrome de surentraînement (overtraining syndrome) demande des semaines à des mois de repos complet.",
    },
  ],
  references: [
    { id: 1, text: "Gabbett TJ. The training-injury prevention paradox: the acute:chronic workload ratio. Br J Sports Med. 2016;50(5):273-80.", pubmed: "26758673" },
    { id: 2, text: "Dwivedi V, et al. Sleep duration and injury risk in adolescent athletes. (méta-analyse récente)" },
    { id: 3, text: "AAP Council on Sports Medicine. Resistance training in children and adolescents. Pediatrics. 2008;121(4).", pubmed: "18381536" },
  ],
};
